import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { DataGrid, GridEnrichedColDef, GridRowModel, GridRowParams, GridSlotsComponent, GridSortModel, useGridApiContext } from '@mui/x-data-grid';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { DataSourceMode, MessageType } from '../../enums.generated';
import { callFunction } from '../../functions/call-function';
import { getProperty, getTypedProperty } from '../../functions/get-property';
import { addClass } from '../../functions/string';
import { Message } from '../../models/app';
import { DataGridBoundEventArgs, DataGridErrorEventArgs, DataGridOptions, DataGridRefreshArgs, DataGridState } from '../../models/data-grid';
import { ApiUrl } from '../../models/http';
import { Guid } from '../../util/Guid';
import { AppTheme } from '../AppTheme';
import { Alert } from './Alert';

/**
 * Data grid component properties, enabling configuration of the data grid columns, sorting, data binding and paging.
 */
type DataGridProps = {
	options: DataGridOptions,
	columns: Array<GridEnrichedColDef>,
	sortModel: GridSortModel,
	getData: (apiUrl: ApiUrl, options: DataGridOptions, state: DataGridState) => Promise<any>,
	onRendering: (options: DataGridOptions, state: DataGridState, refresh: (args: DataGridRefreshArgs) => void, redraw: () => void) => Array<() => void>,
	onRendered: (options: DataGridOptions, state: DataGridState, refresh: (args: DataGridRefreshArgs) => void) => () => void;
	element: Element;
};

const NoRecordsOverlay = (text?: string) => {
	return (
		<div className="no-results">
			{text ?? "No records found"}
		</div>
	);
}

const CustomPagination = (options: DataGridOptions, state: DataGridState) => {
	const apiRef = useGridApiContext();
	const pageNumber = state.page + 1;
	const start = state.pageSize * state.page + 1;
	const nextStart = start + state.pageSize;
	const end = nextStart > state.totalRecords ? state.totalRecords : nextStart - 1;
	let rowsPerPage: JSX.Element | undefined;

	// on page dropdown change handler
	const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedValue = event.currentTarget.selectedOptions[0].value;
		apiRef.current.setPageSize(parseInt(selectedValue));
	};

	// create page size dropdown
	if (options.pageSizeOptions) {
		const pageSizeOptions = options.pageSizeOptions.map(count => <option key={count} value={count}>{count}</option>)
		rowsPerPage = <select className="custom-select custom-select-sm" onChange={onChange} defaultValue={state.pageSize}>{pageSizeOptions}</select>;
	}

	const totalPages = Math.ceil(state.totalRecords / state.pageSize);
	const navDisabled = state.totalRecords <= state.pageSize;

	// create page size container element
	const pageSizeContainer = rowsPerPage ? <div className="rows-per-page">
		Page Size: {rowsPerPage}
	</div> : undefined;

	// output Pagination compoonent along with page size element
	return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={AppTheme}>
                <div className="paging-container">
                    {pageSizeContainer}
                    <div className="paging-info">
						{start}-{end} of {state.totalRecords}
                    </div>
                    <Pagination
                        className="paging-nav"
                        color="primary"
                        disabled={navDisabled}
                        variant="outlined"
                        shape="rounded"
						page={pageNumber}
						count={totalPages}
						onChange={(event, value) => apiRef.current.setPage(value - 1)}
                    />
                </div>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

/**
 * Data grid component, displays tabular data, allowing column sorting, data pagination and more.
 * @param props The data grid properties used to configure the data grid displayed.
 * @see DataGrid
 */
export const DataGridComponent: FunctionComponent<DataGridProps> = (props: DataGridProps) => {

	// setup the data grid state
	const [state, setState] = useState({
		columns: props.columns,
		data: props.options.data ?? new Array<any>(),
		loading: !props.options.data,
		page: 0,
		pageSize: (props.options.dataMode === DataSourceMode.Server ? props.options.apiModel?.paging.pageSize : props.options.pageSize) ?? props.options.pageSize,
		sortModel: props.sortModel,
		totalRecords: props.options.data?.length ?? 0,
		apiBindingEnabled: props.options.autoBind !== false
	} as DataGridState);

	props.options.getState = () => state;

	const tearDownFuncs = Array<() => void>();

	// effects to be executed after component is rendered
	useEffect(() => {
		tearDownFuncs.push(props.onRendered(props.options, state, refreshGrid));
		return () => {
			tearDownFuncs.forEach(func => func());
		};
	});

	const refreshState = () => {

		// refresh state by copying to new object
		setState({ ...state });
	};

	/**
	 * Refresh grid handler. Called to update the data source of an API bound grid.
	 * @param args Refresh args.
	 */
	const refreshGrid = (args: DataGridRefreshArgs) => {

		// reset page index if args to do so
		if (args.resetPageIndex) {
			state.page = 0;
		}

		// set state to loading so loader is displayed while data is reloaded
		state.loading = true;

		if (!state.apiBindingEnabled) {
			state.apiBindingEnabled = true;
		}

		refreshState();
	}

	/**
	 * Page change handler.
	 * @param page The new page number.
	 */
	const handlePageChange = (page: number) => {
		if (page !== state.page) {
			state.page = page;
			state.loading = true;
			refreshState();
		}
	};

	/**
	 * Page size change handler.
	 * @param pageSize The new page size.
	 */
	const handlePageSizeChange = (pageSize: number) => {
		if (pageSize !== state.pageSize) {
			state.pageSize = pageSize;
			state.loading = true;
			refreshState();
		}
	};

	/**
	 * Sort change handler, triggered when column headers are clicked.
	 * @param params Sort change parameters.
	 */
	const handleSortModelChange = (params: GridSortModel) => {
		if (!state.loading) {
			state.sortModel = params;
			state.loading = true;
			refreshState();
		}
	};

	// call on rendering provided by caller and then execute any functions to tear down any  previously rendered elements or behaviours added.
	props.onRendering(props.options, state, refreshGrid, refreshState).forEach(func => tearDownFuncs.push(func));

	// if loading flag is true and API binding enabled, get the data
	if (state.loading === true && props.options.apiUrl && state.apiBindingEnabled) {

		state.apiResult = undefined;

		// call the get data method provided by the call, it will return a promise
		const responsePromise = props.getData(props.options.apiUrl, props.options, state);

		responsePromise
			.then(response => {

				// if successful update data grid state and display the data
				if (response) {

					state.apiResult = response;

					if (response instanceof Array) {

						state.data = response;
						state.totalRecords = response.length;
					}
					else if (props.options.apiProperty) {

						state.data = getTypedProperty<Array<any>>(response, props.options.apiProperty);
						state.totalRecords = response.count;
					}
					else {

						state.data = [];
					}
				}

				// hide the loader
				state.loading = false;

				refreshState();
			})
			.catch(() => {

				if (props.options.onGridError) {

					callFunction(props.options.onGridError, {
						options: props.options,
						element: props.element
					} as DataGridErrorEventArgs);
				}
			});
	}

	// ensure there is an 'id' property as this is used by the grid internally to identify rows
	state.data.forEach(item => {
		if (!item.id) {
			item.id = Guid.newGuid();
		}
	});

	// if API binding completed or data was provided (client-side binding) then revert loading state to false
	if ((props.options.data || !state.apiBindingEnabled) && state.loading === true) {
		state.loading = false;
	}

	// determine the minimum page size
	const minPageSize = props.options.pageSizeSelectionEnabled && props.options.pageSizeOptions ?
		props.options.pageSizeOptions[0] :
		state.pageSize;

	// hide footer pagination if total records is less than min page size
	const hideFooterPagination = state.totalRecords ?
		state.totalRecords <= minPageSize :
		state.data.length <= minPageSize;

	const dataMode = props.options.dataMode === DataSourceMode.Server ? "server" : "client";
	const pageSizes = props.options.pageSizeSelectionEnabled ? props.options.pageSizeOptions : undefined;

	// custom no records component
	const components: Partial<GridSlotsComponent> = {
		NoRowsOverlay: () => NoRecordsOverlay(props.options.noResultsMessage)
	};

	// custom pagination
	if (!hideFooterPagination) {
		components.Pagination = () => CustomPagination(props.options, state);
	}

	// create title element if applicable
	const titleElement = props.options.title ? <h3 className={props.options.titleCssClass}>{props.options.title}</h3> : null;
	let cssClass = props.options.cssClass ?? "";

	// add hidden class if hidden until bound and using API binding
	if (props.options.hiddenUntilBound && !state.apiBindingEnabled) {
		cssClass = addClass(cssClass, "hide");
	}

	// if displaying fresh data call the on grid data bound handler
	if (!state.loading && (props.options.data || state.apiBindingEnabled) && props.options.onGridDataBound) {
		callFunction(props.options.onGridDataBound, {
			data: state.data,
			element: props.element
		} as DataGridBoundEventArgs);
	}

	// row selection handling
	let rowSelected = props.options.onRowSelected ? (params: GridRowParams) => {
		callFunction(props.options.onRowSelected, params);
	} : undefined;

	// determine whether grid should be down or a no results message alert
	const showGrid = (!props.options.noResultsMessage || state.loading) || state.data.length > 0;
	let noResultsMessage: string | undefined;
	if (!showGrid) {

		if (state.apiResult && state.apiResult.noResultsMessage) {

			noResultsMessage = state.apiResult.noResultsMessage;
		}
		else {

			noResultsMessage = props.options.noResultsMessage;
		}
	}

	// build component options
	const gridElement = showGrid ? <DataGrid
		autoHeight={true}
		components={components}
		columns={state.columns}
		disableSelectionOnClick={true}
		hideFooterPagination={hideFooterPagination}
		loading={state.loading}
		onPageChange={handlePageChange}
		onPageSizeChange={handlePageSizeChange}
		onSortModelChange={handleSortModelChange}
		page={state.page}
		pageSize={state.pageSize}
		paginationMode={dataMode}
		rowCount={state.totalRecords}
		rows={state.data}
		rowsPerPageOptions={pageSizes}
		sortingMode={dataMode}
		sortModel={state.sortModel}
		className={state.loading ? "grid-loading" : ""}
		onRowClick={rowSelected}
		disableVirtualization={true}
		getRowId={props.options.keyField ? (row: GridRowModel) => getProperty(row, props.options.keyField ?? "id") : undefined}
	/> : <Alert options={{ type: MessageType.Warning, text: noResultsMessage } as Message}/>;

	// output component
	return (
		<div className={cssClass} style={{ minHeight: 100 }}>
			{titleElement}
			{gridElement}
		</div>
    );
}
